import { z } from 'zod'

import { labTypeSchema } from './lab'

export const procedureTypeSchema = z.enum(['neuter', 'xray', 'ultrasound', 'sedation', 'dental'])
export type ProcedureType = z.infer<typeof procedureTypeSchema>

export const PROCEDURE_TYPE_LABELS: Record<ProcedureType, string> = {
  neuter: 'Neuter',
  xray: 'X-Ray',
  ultrasound: 'Ultrasound',
  sedation: 'Sedation',
  dental: 'Dental',
}

export const PROCEDURE_TYPES = procedureTypeSchema.options.map((value) => ({
  value,
  label: PROCEDURE_TYPE_LABELS[value],
}))

// ─── Task comment ───────────────────────────────────────────────────

export const taskCommentSchema = z.object({
  /** The acting staff member; `null` for System comments raised without one (e.g. a payment webhook). */
  staffId: z.uuid().nullable(),
  authorName: z.string(),
  body: z.string().min(1),
  createdAt: z.string(),
})

export type TaskComment = z.infer<typeof taskCommentSchema>

// ─── Task type ──────────────────────────────────────────────────────

export const taskTypeSchema = z.enum([
  'follow_up',
  'labs',
  'medication_request',
  'prescription_request',
  'referral',
  'wet_script_request',
  'procedure_request',
  'other',
])
export type TaskType = z.infer<typeof taskTypeSchema>

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  follow_up: 'Follow Up',
  labs: 'Labs',
  medication_request: 'Medication Request',
  prescription_request: 'Prescription Request',
  referral: 'Referral',
  wet_script_request: 'Wet Script Request',
  procedure_request: 'Procedure Request',
  other: 'Other',
}

export const TASK_TYPES = taskTypeSchema.options.map((value) => ({
  value,
  label: TASK_TYPE_LABELS[value],
}))

/** Task types created by the system or a dedicated flow, never from the generic task form. */
const SYSTEM_TASK_TYPES: readonly TaskType[] = ['wet_script_request', 'procedure_request']

/** Task types that can be manually created by staff via the UI. */
export const CREATABLE_TASK_TYPES = TASK_TYPES.filter((t) => !SYSTEM_TASK_TYPES.includes(t.value))

export type CreatableTaskType = Exclude<TaskType, 'wet_script_request' | 'procedure_request'>

// ─── Task status ────────────────────────────────────────────────────

export const taskStatusSchema = z.enum(['open', 'in_progress', 'completed', 'cancelled'])
export type TaskStatus = z.infer<typeof taskStatusSchema>

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'To Do',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/**
 * Statuses a task list can be filtered by. `snoozed` is never stored: an open or in-progress
 * task with a future `snoozedUntil` lists under it instead of its stored status, so the default
 * lists hide snoozed tasks and a snooze expires on its own.
 */
export const taskListStatusSchema = z.enum([...taskStatusSchema.options, 'snoozed'])
export type TaskListStatus = z.infer<typeof taskListStatusSchema>

export const TASK_LIST_STATUSES = taskListStatusSchema.options.map((value) => ({
  value,
  label: value === 'snoozed' ? 'Snoozed' : TASK_STATUS_LABELS[value],
}))

/**
 * Labels for a procedure request's lifecycle, which reuses the generic task statuses:
 * open = awaiting the fee, in_progress = ready to book, completed = booked.
 */
export const PROCEDURE_REQUEST_STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Awaiting Payment',
  in_progress: 'Ready',
  completed: 'Booked',
  cancelled: 'Cancelled',
}

// ─── Type-specific data schemas (discriminated union) ───────────────

const followUpDataSchema = z.object({
  type: z.literal('follow_up'),
  dueDate: z.iso.date().optional(),
})

const labsDataSchema = z.object({
  type: z.literal('labs'),
  urgent: z.boolean(),
  // Lab type is optional — staff may create a labs task before the sample type is known.
  labType: labTypeSchema.optional(),
  dueDate: z.iso.date().optional(),
})

const medicationRequestDataSchema = z.object({
  type: z.literal('medication_request'),
  urgent: z.boolean(),
  dueDate: z.iso.date().optional(),
})

const prescriptionRequestDataSchema = z.object({
  type: z.literal('prescription_request'),
  urgent: z.boolean(),
  dueDate: z.iso.date().optional(),
})

const referralDataSchema = z.object({
  type: z.literal('referral'),
  urgent: z.boolean(),
  dueDate: z.iso.date().optional(),
})

const wetScriptRequestDataSchema = z.object({
  type: z.literal('wet_script_request'),
  urgent: z.boolean(),
  /** Snapshot of the treatment that triggered this task. */
  treatment: z
    .object({
      id: z.string().uuid(),
      productName: z.string(),
      quantity: z.number(),
      instructions: z.string(),
      createdAt: z.string(),
    })
    .nullable(),
  dueDate: z.iso.date().optional(),
})

/**
 * The fields of a task whose work is gated on a fee: the task is Ready once the invoice is paid,
 * and cancelling it voids or refunds the invoice.
 */
export const invoicedTaskDataSchema = z.object({
  /** Snapshot of the fee at creation. `null` when no fee applied. */
  fee: z.object({ amount: z.number().int(), currency: z.string() }).nullable(),
  stripeInvoiceId: z.string().nullable(),
  /** Stripe hosted payment page, for staff to copy or show as a QR while unpaid. */
  hostedInvoiceUrl: z.string().nullable(),
  paidAt: z.string().nullable(),
  /** Set when cancelling the task refunded the paid fee. Absent on tasks cancelled before refunds existed. */
  refundedAt: z.string().nullable().optional(),
})

export type InvoicedTaskData = z.infer<typeof invoicedTaskDataSchema>

const procedureRequestDataSchema = invoicedTaskDataSchema.extend({
  type: z.literal('procedure_request'),
  urgent: z.boolean().default(false),
  procedure: procedureTypeSchema,
  /** The requesting vet's note. Shown on the entry and in the Slack message. */
  note: z.string().min(1).max(1000),
})

export type ProcedureRequestData = z.infer<typeof procedureRequestDataSchema>

const otherDataSchema = z.object({
  type: z.literal('other'),
  urgent: z.boolean(),
  dueDate: z.iso.date().optional(),
})

export const taskDataSchema = z.discriminatedUnion('type', [
  followUpDataSchema,
  labsDataSchema,
  medicationRequestDataSchema,
  prescriptionRequestDataSchema,
  referralDataSchema,
  wetScriptRequestDataSchema,
  procedureRequestDataSchema,
  otherDataSchema,
])

export type TaskData = z.infer<typeof taskDataSchema>

/** Data schema for tasks created through the generic task form — excludes `procedure_request`, which has its own creation path. */
export const genericTaskDataSchema = z.discriminatedUnion('type', [
  followUpDataSchema,
  labsDataSchema,
  medicationRequestDataSchema,
  prescriptionRequestDataSchema,
  referralDataSchema,
  wetScriptRequestDataSchema,
  otherDataSchema,
])

// ─── Base task entity ───────────────────────────────────────────────

export interface Task {
  id: string
  type: TaskType
  title: string
  status: TaskStatus
  patientId: string | null
  createdByStaffId: string | null
  assigneeRole: string | null
  assigneeClinicId: string | null
  assigneeStaffId: string | null
  data: TaskData
  comments: TaskComment[]
  snoozedUntil: string | null
  createdAt: string
  updatedAt: string
}

/** Whether a task is currently parked by a snooze, mirroring the list filter's `snoozed` bucket. */
export function isTaskSnoozed(task: {
  status: TaskStatus
  snoozedUntil: Date | string | null
}): boolean {
  const active = task.status === 'open' || task.status === 'in_progress'
  return active && !!task.snoozedUntil && new Date(task.snoozedUntil) > new Date()
}

/** Status label for a specific task — procedure requests relabel the generic statuses. */
export function getTaskStatusLabel(task: { type: TaskType; status: TaskStatus }): string {
  return task.type === 'procedure_request'
    ? PROCEDURE_REQUEST_STATUS_LABELS[task.status]
    : TASK_STATUS_LABELS[task.status]
}
